enum ComponentName {
    'gallery' = 'Galeria zdjęć',
    'premium' = 'Premium',
    'related-articles' = 'Powiązane artykuły',
    'survey' = 'Ankieta'
}

interface FormField {
    type: string;
    name: string;
    label: string;
    items?: Array<{ value: string; text: string }>;
    filetype?: string;
    [key: string]: any;
}

interface ComponentConfig {
    name: string | ((data: Record<string, any>) => string);
    fields?: (data: Record<string, any>) => FormField[];
    defaultData?: Record<string, any>;
}

const substituteName = 'substitute';
const substituteRemoveName = 'substituteremove';
const substituteEditName = 'substituteedit';

const SubstituteInit = (tinymce: any): any =>
{
    tinymce.PluginManager.add(substituteName, function(editor: any)
    {
        const style = `div[data-component] {
            background: #e7e7e7;
            padding: 10px;
            border-radius: 5px;
            text-align: center;
            box-shadow: 0 0 10px -9px black;
            cursor: pointer;
            margin-bottom: 20px;
        }`;

        editor.contentStyles.push(style);

        let currentEditElement: HTMLDivElement | null = null;

        // Konfiguracja komponentów
        const componentConfigs: Record<string, ComponentConfig> = {
            youtube: {
                name: 'YouTube',
                defaultData: { url: '' },
                fields: () => [
                    { type: 'input', name: 'url', label: 'Link' }
                ]
            },
            gallery: {
                name: (data: Record<string, any>): string => {
                    switch (data.mode) {
                        case 'article':
                            return $t('[[[Multimedia przypisane do artykułu]]]');
                        case 'premium':
                            return $t('[[[Multimedia premium]]]');
                        case 'manual':
                        default:
                            return $t('[[[Galeria zdjęć]]]');
                    }
                },
                defaultData: { mode: 'article' },
                fields: (data: Record<string, any>): FormField[] => {
                    const baseFields: FormField[] = [
                        {
                            type: 'selectbox',
                            name: 'mode',
                            label: $t('[[[Tryb galerii]]]'),
                            items: [
                                { value: 'article', text: $t('[[[Multimedia przypisane do artykułu]]]') },
                                { value: 'premium', text: $t('[[[Multimedia premium]]]') },
                                { value: 'manual', text: $t('[[[Ręczny wybór zdjęć lub galerii]]]') }
                            ]
                        }
                    ];

                    // Dynamiczne pola w zależności od wybranego trybu
                    switch (data.mode) {
                        case 'manual':
                            baseFields.push(
                                {
                                    type: 'urlinput',
                                    name: 'images',
                                    label: $t('[[[Zdjęcia]]]'),
                                    filetype: 'image'
                                }
                            );
                            break;
                    }

                    return baseFields;
                }
            },
            'related-articles': {
                name: 'Powiązane artykuły',
            },
            survey: {
                name: 'Ankieta',
            }
        };

        const openDialog = (initialData: Record<string, any> = {}) =>
        {
            const component = initialData.component;
            const config = componentConfigs[component];

            if (!config) {
                console.warn(`Component configuration not found for: ${component}`);
                return;
            }

            const mergedData = { ...config.defaultData, ...initialData };
            let currentData = { ...mergedData };

            const buildItems = (comp: string, data: Record<string, any>): FormField[] =>
            {
                const compConfig = componentConfigs[comp];
                return compConfig ? compConfig.fields(data) : [];
            }

            const handleSubmit = (api?: any) =>
            {
                const comp = currentEditElement?.dataset.component ?? component;
                const { component: _, ...params } = api ? api.getData() : currentData;
                const props = JSON.stringify(params).replace(/"/g, "'");

                if (currentEditElement)
                {
                    currentEditElement.dataset.component = comp;
                    currentEditElement.dataset.props = props;
                    currentEditElement.innerHTML = comp.toUpperCase();
                    currentEditElement = null;
                }
                else
                {
                    const displayName = typeof config.name === 'function'
                        ? config.name(currentData)
                        : config.name;
                    editor.insertContent(`<div data-component="${comp}" data-props="${props}">${displayName}</div><p></p>`);
                }

                api?.close();
                registerClick();
            }

            if (['related-articles', 'survey'].includes(component))
            {
                handleSubmit();

                return;
            }

            const displayName = typeof config.name === 'function'
                ? config.name(currentData)
                : config.name;

            const modalSettings = {
                title: displayName,
                body: {
                    type: 'panel',
                    items: buildItems(component, currentData)
                },
                initialData: {
                    component,
                    ...currentData
                },
                buttons: [
                    { type: 'cancel', text: 'Close' },
                    { type: 'submit', text: 'Save', primary: true }
                ],
                onSubmit: handleSubmit,
                onChange: function(dialogApi: any)
                {
                    const data = dialogApi.getData();
                    currentData = { ...data };

                    modalSettings.body.items = buildItems(component, data);
                    modalSettings.initialData = { ...data };

                    dialogApi.redial(modalSettings);
                }
            };

            editor.windowManager.open(modalSettings);
        };

        editor.openSubstituteDialog = openDialog;

        const signCurrentElement = function (this: HTMLDivElement): void {
            currentEditElement = this;
        };

        const registerClick = () => {
            unregisterClick();
            const elements = editor.contentDocument.querySelectorAll('div[data-component]');

            elements.forEach((el: HTMLDivElement) => el.addEventListener('click', signCurrentElement));
        };

        const unregisterClick = () => {
            const elements = editor.contentDocument.querySelectorAll('div[data-component]');

            elements.forEach((el: HTMLDivElement) => el.removeEventListener('click', signCurrentElement));
        };

        editor.on('init', registerClick);
        editor.on('remove', unregisterClick);

        editor.ui.registry.addContextToolbar('substituteContextToolbar', {
            predicate: (node: Node) => node.nodeName === 'DIV' && (node as HTMLElement).hasAttribute('data-component'),
            items: 'substituteedit substituteremove',
            position: 'node',
            scope: 'node'
        });

        editor.ui.registry.addButton(substituteEditName, {
            icon: 'edit-block',
            tooltip: $t('[[[Edytuj shortcode]]]'),
            onAction() {
                const { component = '', props = '' } = currentEditElement.dataset;
                const data = { component, ...JSON.parse(props.replace(/'/g, '"')) };
                openDialog(data);
                closeAllMenus(editor);
                currentEditElement = null;
            },
            onSetup(api: any) {
                const node = editor.selection.getNode();
                const component = node.getAttribute('data-component');
                api.setEnabled(!['related-articles', 'survey'].includes(component));
                return () => {};
            }
        });

        editor.ui.registry.addButton(substituteRemoveName, {
            icon: 'remove',
            tooltip: $t('[[[Usuń shortcode]]]'),
            onAction() {
                editor.selection.getNode()?.remove();
                closeAllMenus(editor);
                currentEditElement = null;
            }
        });

        const obsoleteShortcodes = [
            { name: 'sc-gallery', icon: 'image', text: $t('[[[Galeria zdjęć]]]') },
            { name: 'sc-youtube', icon: 'embed', text: $t('[[[Film z YouTube]]]')},
            { name: 'sc-related-articles', icon: 'document-properties', text: $t('[[[Powiązane artykuły]]]') },
            { name: 'sc-survey', icon: 'ordered-list', text: $t('[[[Ankieta]]]') }
        ];

        obsoleteShortcodes.forEach(({ name, icon, text }) =>
        {
            const component = name.replace('sc-', '');

            editor.ui.registry.addMenuItem(name, {
                icon,
                text,
                onAction: () => openDialog({ component }),
            });
        });

        return {
            getMetadata: () => ({
                name: 'Shortcode plugin',
                url: 'https://docs.ideo.pl/ideo_docs/ideocore/zarzadzanie/'
            })
        };
    });
}

const closeAllMenus = (editor: any): void => {
    document.querySelectorAll('.tox-collection--toolbar, .tox-menu, .tox-context-menu, .tox-tiered-menu, .tox-pop')
        .forEach(menu => (menu as HTMLElement).style.display = 'none');

    document.body.click();
    editor.selection.collapse();
};

export { SubstituteInit, substituteName };
